import React, { HTMLProps, useMemo } from 'react';
import { FundPieChart } from './FundPieChart';
import { FundCompareList } from './FundCompareList';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';

export const FundCompareChartSection: React.FC<
    {
        sections: { title?: string; items: { tint: string; id: string; name: string; weight: number }[] }[];
        title: string;
        showColoredCircles?: boolean;
        bottomBorder?: boolean;
        appendix?: 'total' | 'remaining' | 'none';
        showChart?: boolean;
        keyString: string;
        fundCompareListClassName?: string;
        referenceDate?: DateTime;
    } & HTMLProps<HTMLDivElement>
> = ({
    className,
    fundCompareListClassName,
    sections,
    title,
    showChart = true,
    keyString,
    showColoredCircles = true,
    bottomBorder = false,
    referenceDate,
    appendix = 'none',
    ...rest
}) => {
    const { t } = useTranslation('funds');

    const total = useMemo(() => {
        if (appendix === 'none') return 0;
        return (
            Math.floor(
                sections.reduce((total, section) => {
                    return (
                        total +
                        section.items.reduce((total, item) => {
                            return total + item.weight;
                        }, 0)
                    );
                }, 0) * 10,
            ) / 10
        );
    }, [appendix, sections]);

    sections[0].items =
        appendix === 'remaining' && sections[0]?.items[sections[0].items.length - 1]?.name !== 'Overig'
            ? sections[0].items.concat({ tint: '#9CA3AF', id: 'Overig', name: 'Overig', weight: 100 - total })
            : sections[0].items;

    if (sections.length === 0) {
        return <div className={`flex flex-col flex-1 min-w-0 ${className}`}>Geen informatie</div>;
    }

    return (
        <div className={`flex flex-col flex-1 min-w-0 ${className}`}>
            {referenceDate && (
                <div className="p-2 text-center text-xs text-gray-400">
                    {t('funds-comparison-screen-reference-date')}:{' '}
                    {referenceDate?.setLocale('nl').toFormat('d MMM yyyy')}
                </div>
            )}
            {showChart && (
                <div className="mb-4 py-4 relative grid grid-cols-1 grid-rows-1" style={{ minHeight: '280px' }}>
                    <FundPieChart sections={sections} title={title} />
                </div>
            )}
            <FundCompareList
                className={`py-4 px-0 ${fundCompareListClassName}`}
                sections={sections}
                showColoredCircles={showColoredCircles}
                appendix={appendix}
            />
        </div>
    );
};
